import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from 'recharts';
import {
    timeSeriesXAxis,
    colors,
    lineGridStyle,
} from '../../../../shared/constants/constants';
import CustomTooltip from '../../CustomTooltip';
import {
    getXAxisLabel,
    getYAxisLabel,
    getChartMargin,
    getFormattedValue,
} from '../../../dashboardUtils';

function TimeSeriesBarGraph({
    chartProperties,
    dataFormatter,
    dataLabels,
    dataPoints,
    dateFormatter,
    legendFormatter,
    thresholds,
    xAxisLabel,
    yAxisLabelLeft,
}) {
    const [activeIndex, setActiveIndex] = useState(-1);
    const {
        barChartProperties,
        barProperties,
        colorPalette = colors,
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend,
        showSinglePointTooltip,
        suffix = '',
        timeFormat,
        tooltipProperties,
        xAxisProperties,
        yAxisProperties,
    } = chartProperties;

    const yAxisTickFormatter = (value) =>
        getFormattedValue(value, suffix, dataFormatter, timeFormat);

    const chartMargin = getChartMargin(
        dataPoints,
        timeSeriesXAxis,
        suffix,
        thresholds,
        timeFormat,
        yAxisTickFormatter,
        yAxisLabelLeft
    );

    return (
        <ResponsiveContainer {...responsiveContainerProperties}>
            <BarChart {...barChartProperties} data={dataPoints}>
                <CartesianGrid strokeDasharray={lineGridStyle} />
                <XAxis
                    {...xAxisProperties}
                    dataKey={timeSeriesXAxis}
                    tickFormatter={dateFormatter}
                    label={getXAxisLabel(xAxisLabel, xAxisProperties)}
                />
                <YAxis
                    {...yAxisProperties}
                    label={getYAxisLabel(yAxisLabelLeft)}
                    tickFormatter={yAxisTickFormatter}
                    width={chartMargin.left}
                />
                <Tooltip
                    {...tooltipProperties}
                    cursor={dataPoints.length !== 0}
                    content={
                        <CustomTooltip
                            activeIndex={activeIndex}
                            axisFormatter={dateFormatter}
                            axisKey={timeSeriesXAxis}
                            dataFormat={timeFormat}
                            dataFormatter={dataFormatter}
                            legendFormatter={legendFormatter}
                            showSinglePoint={showSinglePointTooltip}
                            suffix={suffix}
                            thresholds={thresholds}
                        />
                    }
                />
                {showLegend && (
                    <Legend {...legendProperties} formatter={legendFormatter} />
                )}
                {dataLabels.map((dataLabel, index) => (
                    <Bar
                        {...barProperties}
                        key={dataLabel}
                        dataKey={dataLabel}
                        fill={colorPalette[index % colorPalette.length]}
                        onMouseOver={() => setActiveIndex(index)}
                        onMouseOut={() => setActiveIndex(-1)}
                        onFocus={() => setActiveIndex(index)}
                        onBlur={() => setActiveIndex(-1)}
                    />
                ))}
                {thresholds.map((threshold) => (
                    <ReferenceLine
                        key={`threshold-${threshold.id}`}
                        {...referenceLineProperties}
                        y={threshold.value}
                        stroke={threshold.lineColor}
                    />
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
}

TimeSeriesBarGraph.propTypes = {
    chartProperties: PropTypes.instanceOf(Object).isRequired,
    dataFormatter: PropTypes.func,
    dataLabels: PropTypes.instanceOf(Array),
    dataPoints: PropTypes.instanceOf(Array),
    dateFormatter: PropTypes.func.isRequired,
    legendFormatter: PropTypes.func.isRequired,
    thresholds: PropTypes.instanceOf(Array).isRequired,
    xAxisLabel: PropTypes.string,
    yAxisLabelLeft: PropTypes.string,
};

TimeSeriesBarGraph.defaultProps = {
    dataPoints: [],
    dataLabels: [],
    xAxisLabel: null,
    yAxisLabelLeft: null,
};

export default React.memo(
    TimeSeriesBarGraph,
    (prevProps, nextProps) =>
        _.isEqual(prevProps.dataPoints, nextProps.dataPoints) &&
        _.isEqual(prevProps.thresholds, nextProps.thresholds)
);
